import React, { memo } from 'react';
import { Handle } from 'react-flow-renderer';
import { Chip, Tooltip, IconButton } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { ScheduleOutlined as ScheduleIcon, GridOnOutlined as GridOn, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { NodeWrapper } from './NodeWrapper';
import { mkHeaderStyle, HEADER_ICON_STYLE, HEADER_TEXT_STYLE, NODE_BODY_STYLE } from './nodeStyles';
import { i18n } from "../../../translate/i18n"; 

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    maxWidth: 380,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #334155',
    padding: '12px 14px',
    borderRadius: '8px',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    lineHeight: 1.5,
  },
  arrow: {
    color: '#1e293b',
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  pulseIcon: {
    color: '#ffffff',
    borderRadius: '50%',
    animation: '$glowPulse 1.5s infinite',
  },
  '@keyframes glowPulse': {
    '0%': { 
      boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.9)'
    },
    '70%': { 
      boxShadow: '0 0 0 8px rgba(255, 255, 255, 0)'
    },
    '100%': { 
      boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)'
    },
  },
}));

export default memo(({ data, isConnectable, id, selected }) => {
  const classes = useStyles();
  const remarketingEnabled = data.remarketingEnabled !== false;
  const remarketingTasks = data.remarketingTasks || [];
  
  return (
    <NodeWrapper id={data.id || id} selected={selected} color="#06b6d4">
      <Handle
        type="target"
        position="left"
        id="b"
        style={{ background: '#06b6d4', width: '10px', height: '20px', borderRadius: '4px', left: '-12px', border: '2px solid white', top: "20px", zIndex: 1 }}
        isConnectable={isConnectable}
      />

      <div style={{ ...mkHeaderStyle("#06b6d4"), justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ScheduleIcon style={HEADER_ICON_STYLE} />
          <div style={HEADER_TEXT_STYLE}>
            {data.name || "Remarketing"}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {remarketingEnabled && remarketingTasks.length > 0 && (
            <Tooltip title={`${i18n.t("flowBuilder.remarketing.title") || "Tareas"} (${remarketingTasks.length})`}>
              <Chip 
                icon={<ScheduleIcon style={{ fontSize: 13, color: "#0891b2" }} />} 
                label={remarketingTasks.length} 
                size="small" 
                style={{ height: 20, fontSize: '0.7rem', backgroundColor: '#cffafe', color: "#0891b2", fontWeight: "bold" }} 
              />
            </Tooltip>
          )}

          <HtmlTooltip
            title={
              <React.Fragment>
                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#38bdf8' }}>
                  💡 {i18n.t("flowBuilder.tooltips.remarketing.title")}
                </div>
                <div dangerouslySetInnerHTML={{ __html: i18n.t("flowBuilder.tooltips.remarketing.body") }} />
                <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#cbd5e1' }}>
                  * Cualquier mensaje del cliente anula los cronómetros automáticamente.
                </div>
              </React.Fragment>
            }
            placement="right"
          >
            <IconButton size="small" style={{ padding: '4px', marginLeft: '2px' }} className={classes.pulseIcon}>
              <HelpOutlineIcon style={{ fontSize: '15px' }} />
            </IconButton>
          </HtmlTooltip>
        </div>
      </div>

      {/* Salida por defecto (Pass-through) */}
      <div style={{
        position: "relative",
        padding: "8px 12px",
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <span style={{ fontSize: "11px", fontWeight: 600, color: "#475569" }}>
          Continuar Flujo
        </span>
        <Handle
          type="source"
          position="right"
          id="next"
          style={{
            background: "#475569",
            width: "10px",
            height: "20px",
            borderRadius: "4px",
            right: -12,
            cursor: "pointer",
            border: "2px solid white",
            zIndex: 10,
            top: "50%",
            transform: "translateY(-50%)"
          }}
          isConnectable={isConnectable}
        />
      </div>

      {remarketingEnabled && remarketingTasks.length > 0 && (
        <div style={{ borderTop: "1px solid #f1f5f9" }}>
          <div style={{
            padding: "8px 12px",
            backgroundColor: "#cffafe",
            fontSize: "11px",
            fontWeight: 400,
            fontFamily: "'Inter', 'Roboto', sans-serif",
            color: "#0e7490",
            
          }}>
            {i18n.t("flowBuilder.remarketing.title") || "TAREAS DE REMARKETING"}
          </div>

          {[...remarketingTasks].sort((a, b) => (a.order || 0) - (b.order || 0)).map((task, index, array) => {
            if (!task) return null;
            const isFinal = task.isFinalTask || task.closeTicket;
            const isLastItem = index === array.length - 1;
            const isInactive = task.active === false;

            const bgStyle = isFinal ? "#fff0f5" : "white";
            return (
              <div key={`${task.id}-${index}`} style={{
                position: "relative",
                padding: "8px 20px 8px 12px",
                borderBottom: !isLastItem ? "1px solid #f1f5f9" : "none",
                borderLeft: isFinal ? "4px solid #e91e63" : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: bgStyle,
                fontSize: "12px",
                color: "#334155",
                borderBottomLeftRadius: isLastItem ? "10px" : "0",
                borderBottomRightRadius: isLastItem ? "10px" : "0",
                opacity: isInactive ? 0.6 : 1,
                textDecoration: isInactive ? "line-through" : "none"
              }}>
                <div style={{
                  flex: 1,
                  marginRight: "40px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  overflow: 'hidden'
                }}>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: "30px",
                    gap: "4px"
                  }}>
                    {isFinal ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <GridOn style={{ width: "14px", height: "14px", color: "#880e4f" }} />
                        <span style={{ fontWeight: "bold", fontFamily: "'Inter', 'Roboto', sans-serif", color: "#e91e63", fontSize: "12px" }}>
                          {i18n.t("flowBuilder.remarketing.fin") || "FIN"}
                        </span>
                        <span style={{ fontFamily: "'Inter', 'Roboto', sans-serif", fontSize: '11px', fontStyle: 'italic', color: '#e91e63', marginLeft: "8px" }}>
                          {i18n.t("flowBuilder.remarketing.autoClose") || "Auto-Cierre"}
                        </span>
                      </div>
                    ) : (
                      <span style={{ fontWeight: 500, fontFamily: "'Inter', 'Roboto', sans-serif", color: "#0e7490", fontSize: "11px" }}>
                        {isInactive ? i18n.t("flowBuilder.remarketing.inactiveTask") || "[INACTIVA]" : `#${task.order || index + 1}`}
                      </span>
                    )}
                    <span style={{ fontFamily: "'Inter', 'Roboto', sans-serif", fontSize: "11px", color: isFinal ? "#e91e63" : "#64748b" }}>
                      {task.waitUnit === 'days' ? `${task.waitTime}d` : task.waitUnit === 'hours' ? `${task.waitTime}h` : task.waitUnit === 'minutes' ? `${task.waitTime}m` : `${task.waitTime}m`}
                    </span>
                  </div>

                  {task.variable && !isFinal && (
                    <Chip 
                      label={task.variable} 
                      size="small" 
                      style={{
                        height: 22,
                        fontSize: '0.75rem',
                        backgroundColor: '#fff',
                        color: '#06b6d4',
                        border: '1px solid #e2e8f0',
                        padding: '0 4px'
                      }} 
                    />
                  )}

                  {isFinal && (
                    <span style={{ fontSize: '10px', fontStyle: 'italic', color: '#ad1457' }}>
                      {i18n.t("flowBuilder.remarketing.automaticTag") || "Automática"}
                    </span>
                  )}
                </div>

                <Handle 
                  type="source" 
                  position="right" 
                  id={`remarketing-${task.order || index}`} 
                  style={{
                    background: isFinal ? "#e91e63" : "#06b6d4",
                    width: "10px",
                    height: "20px",
                    borderRadius: "4px",
                    right: -12,
                    cursor: 'pointer',
                    border: '2px solid white',
                    zIndex: 10, 
                    top: "50%", 
                    transform: "translateY(-50%)"
                  }} 
                  isConnectable={isConnectable} 
                />
              </div>
            );
          })}
        </div>
      )}
    </NodeWrapper>
  );
});
